import { createReducer } from '@reduxjs/toolkit'

import {
  EMPLOYER_VENUE_NAME_FETCH_SUCCEEDED,
  EMPLOYER_VENUE_SEARCH_FETCH_BEGIN,
  EMPLOYER_VENUE_SEARCH_FETCH_FAILED,
  EMPLOYER_VENUE_SEARCH_FETCH_SUCCEEDED,
} from './action-types'
import type { SearchEmployerVenuesAction } from './actions'
import type {
  EmployerVenueSearchState,
  FetchEmployerVenueNameSucceededAction,
  SearchEmployerVenuesFailedAction,
  SearchEmployerVenuesSucceededAction,
  VenueSearchResult,
} from './types'

export const initialState: EmployerVenueSearchState = {
  employerID: null,
  query: '',
  status: 'idle',
  venues: [],
  total: 0,
  namesById: {},
}

const isCurrentSearch = (state: EmployerVenueSearchState, request: SearchEmployerVenuesAction) =>
  request.payload.employerID === state.employerID && request.payload.query === state.query

const withNames = (
  namesById: EmployerVenueSearchState['namesById'],
  venues: ReadonlyArray<VenueSearchResult>,
): EmployerVenueSearchState['namesById'] => ({
  ...namesById,
  ...Object.fromEntries(venues.map(venue => [String(venue.id), venue.name])),
})

export const reducer = createReducer(initialState, builder =>
  builder
    .addCase(
      EMPLOYER_VENUE_SEARCH_FETCH_BEGIN,
      (state, { payload }: SearchEmployerVenuesAction): EmployerVenueSearchState => ({
        ...state,
        employerID: payload.employerID,
        query: payload.query,
        status: 'loading',
        // Previous results stay listed while the next page loads, unless they belong to another employer.
        venues: payload.employerID === state.employerID ? state.venues : [],
        total: payload.employerID === state.employerID ? state.total : 0,
      }),
    )
    .addCase(
      EMPLOYER_VENUE_SEARCH_FETCH_SUCCEEDED,
      (state, action: SearchEmployerVenuesSucceededAction): EmployerVenueSearchState => {
        const venues = action.payload ?? []
        const namesById = withNames(state.namesById, venues)

        if (!isCurrentSearch(state, action.request)) return { ...state, namesById }

        return { ...state, status: 'succeeded', venues, total: action.total ?? venues.length, namesById }
      },
    )
    .addCase(
      EMPLOYER_VENUE_SEARCH_FETCH_FAILED,
      (state, action: SearchEmployerVenuesFailedAction): EmployerVenueSearchState =>
        isCurrentSearch(state, action.request) ? { ...state, status: 'failed' } : state,
    )
    .addCase(
      EMPLOYER_VENUE_NAME_FETCH_SUCCEEDED,
      (state, action: FetchEmployerVenueNameSucceededAction): EmployerVenueSearchState => ({
        ...state,
        namesById: withNames(state.namesById, action.payload ?? []),
      }),
    )
    .addDefaultCase(state => state),
)
